/**
 * CalloutNode - アイコンと背景色で本文を目立たせる Notion 風のコールアウトブロック。
 *
 * 中身は任意のブロック (block+)。アイコンと色はノード属性に持ち、callout-node-view.tsx で変更する。
 */

import { findParentNode, mergeAttributes, Node } from '@tiptap/core'
import type { Node as PMNode } from '@tiptap/pm/model'
import { NodeSelection, type EditorState } from '@tiptap/pm/state'
import { findWrapping, liftTarget } from '@tiptap/pm/transform'
import type { NodeViewProps } from '@tiptap/react'
import { ReactNodeViewRenderer } from '@tiptap/react'

import { CalloutNodeView } from './callout-node-view'
import { type CalloutColor, DEFAULT_CALLOUT_COLOR, DEFAULT_CALLOUT_ICON, toCalloutColor } from './callout-options'

export interface CalloutAttributes {
  icon?: string
  color?: CalloutColor
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    callout: {
      /** 選択中のブロックをコールアウトで囲む。囲めない位置なら空のコールアウトを挿入する */
      setCallout: (attrs?: CalloutAttributes) => ReturnType
      /** 選択中の最上位ブロックをコールアウトで囲む。コールアウト内なら解除する (Turn into 用) */
      toggleCallout: () => ReturnType
      /** 選択位置を含むコールアウトを解除し、中身のブロックをすべて外に出す */
      unsetCallout: () => ReturnType
    }
  }
}

const findCallout = (state: EditorState): { pos: number; node: PMNode } | undefined => {
  const { selection } = state
  if (selection instanceof NodeSelection && selection.node.type.name === 'callout') {
    return { pos: selection.from, node: selection.node }
  }
  return findParentNode((node) => node.type.name === 'callout')(selection)
}

export const CalloutNode = Node.create({
  name: 'callout',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      icon: {
        default: DEFAULT_CALLOUT_ICON,
        parseHTML: (el) => el.getAttribute('data-icon') || DEFAULT_CALLOUT_ICON,
        renderHTML: (attrs) => ({ 'data-icon': String(attrs.icon || DEFAULT_CALLOUT_ICON) }),
      },
      color: {
        default: DEFAULT_CALLOUT_COLOR,
        parseHTML: (el) => toCalloutColor(el.getAttribute('data-color')),
        renderHTML: (attrs) => ({ 'data-color': toCalloutColor(attrs.color) }),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'div[data-type="callout"]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': 'callout' }, HTMLAttributes), 0]
  },

  addNodeView() {
    return ReactNodeViewRenderer(CalloutNodeView as (props: NodeViewProps) => React.JSX.Element)
  },

  addCommands() {
    return {
      setCallout:
        (attrs) =>
        ({ commands }) =>
          commands.wrapIn(this.name, attrs) ||
          commands.insertContent({ type: this.name, attrs, content: [{ type: 'paragraph' }] }),
      toggleCallout:
        () =>
        ({ state, tr, dispatch, commands }) => {
          if (findCallout(state)) return commands.unsetCallout()
          const { $from, $to } = state.selection
          const start = $from.depth > 0 ? $from.before(1) : $from.pos
          const end = $to.depth > 0 ? $to.after(1) : $to.pos
          const range = tr.doc.resolve(start).blockRange(tr.doc.resolve(end))
          const wrapping = range && findWrapping(range, this.type)
          if (!range || !wrapping) return false
          if (dispatch) tr.wrap(range, wrapping)
          return true
        },
      unsetCallout:
        () =>
        ({ state, tr, dispatch }) => {
          const callout = findCallout(state)
          if (!callout) return false
          const range = tr.doc
            .resolve(callout.pos + 1)
            .blockRange(tr.doc.resolve(callout.pos + callout.node.nodeSize - 1))
          const target = range ? liftTarget(range) : null
          if (!range || target == null) return false
          if (dispatch) tr.lift(range, target)
          return true
        },
    }
  },
})
